// 1. React
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { OfferItemsProps } from "./types";
import { Errors } from "services/forms/validation/types";
import { FleetVehicleOfferSaleItemEditFormValues, FleetVehicleOfferSaleItemRow } from "services/api/fleet/vehicles/offers/types";
import { FleetVehicle } from "services/api/fleet/vehicles/types";

// 4. API / Consts
import { initialFleetVehicleOfferSaleItemEditForm } from "services/api/fleet/vehicles/offers/consts";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { transformOfferSaleItemRow, transformOfferSaleItemRowToForm } from "services/api/fleet/vehicles/offers/transformations";

// 5. UI Components
import OfferItemsAdded from "./OfferItemsAdded";
import { OfferItemForm } from "./OfferItemForm";

// 6. Validation
import { validateYup } from "services/forms/validation/validation";
import { FLEET_VEHICLE_OFFER_SALE_ITEM_EDIT_FORM_SCHEMA } from "./validation";
import { Dialog } from "ui/components/Dialog";
import { Button } from "ui/components/Button/Button";


const OfferItems: React.FC<OfferItemsProps> = (props) => {
    const { formValues, setFormValues } = props;

    const { t } = useTranslate();

    const [itemFormAdd, setItemFormAdd] =
        useState<FleetVehicleOfferSaleItemEditFormValues>(
            { ...initialFleetVehicleOfferSaleItemEditForm }
        );

    const [itemId, setItemId] = useState<string>("");
    const [isEdit, setIsEdit] = useState(false);
    const [showItemFormModal, setShowItemFormModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [errors, setErrors] = useState<Errors>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (formValues.vehicleId) fetchVehicle(formValues.vehicleId);
    }, [formValues.vehicleId]);

    const fetchVehicle = async (id: string) => {
        if (id === undefined) {
            return;
        }

        setIsLoading(true);

        let result: FleetVehicle;
        try {
            result = await getFleetVehicleAPI(id);
        } catch {
            setIsLoading(false);
            return;
        }

        if (result && formValues.items && formValues.items.length === 0) {
            const newItems: FleetVehicleOfferSaleItemRow[] = [];

            // Prva stavka - vozilo
            const vehicleItem = transformOfferSaleItemRow({
                ...initialFleetVehicleOfferSaleItemEditForm,
                id: `vehicle-${Date.now()}`,
                code: result.registrationPlate,
                name: result.name,
                quantity: 1,
                taxRate: 25,
                priceWithoutTax: result.price?.sellingPrice ?? 0,
                priceWithTax: (result.price?.sellingPrice ?? 0) * 1.25,
                total: (result.price?.sellingPrice ?? 0) * 1.25,
            });
            newItems.push(vehicleItem);

            // Druga stavka - PPMV (ako postoji specialTax)

            const ppmvItem = transformOfferSaleItemRow({
                ...initialFleetVehicleOfferSaleItemEditForm,
                id: `ppmv-${Date.now()}`,
                code: "PPMV",
                name: "Posebni porez na motorna vozila",
                quantity: 1,
                taxRate: 0,
                priceWithoutTax: result.price?.specialTax ?? 0,
                priceWithTax: result.price?.specialTax ?? 0,
                total: result.price?.specialTax ?? 0,
            });
            newItems.push(ppmvItem);


            setFormValues(prev => ({
                ...prev,
                items: newItems,
            }));
        }


        setIsLoading(false);
        setErrors({});
    };

    const handleAddItem = async (e: any) => {
        e.preventDefault();

        const isValid = validateYup(
            itemFormAdd,
            FLEET_VEHICLE_OFFER_SALE_ITEM_EDIT_FORM_SCHEMA,
            setErrors
        );

        if (!isValid) {
            return;
        }

        setIsLoading(true);

        if (isEdit) {
            setFormValues(prev => ({
                ...prev,
                items: prev.items.map(item =>
                    item.id === itemFormAdd.id
                        ? transformOfferSaleItemRow(itemFormAdd)
                        : item
                ),
            }));
        }
        else {
            setFormValues(prev => ({
                ...prev,
                items: [...prev.items, transformOfferSaleItemRow(itemFormAdd)]
            }));
        }

        setItemFormAdd(initialFleetVehicleOfferSaleItemEditForm);
        setIsEdit(false);
        setItemId("");
        setIsLoading(false);
        setShowItemFormModal(false);
    };

    const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        setItemFormAdd({ ...initialFleetVehicleOfferSaleItemEditForm, id: crypto.randomUUID() });
        setItemId("");
        setIsEdit(false);
        setErrors({});
        setShowItemFormModal(true);
    };
    const handleEditClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        let item = formValues.items.find(item => item.id === id)

        if (!item)
            return;

        setItemFormAdd(transformOfferSaleItemRowToForm(item));
        setItemId(id);
        setIsEdit(true);
        setShowItemFormModal(true);
    }

    const handleItemFormModalClose = () => {
        setShowItemFormModal(false);
        setItemFormAdd(initialFleetVehicleOfferSaleItemEditForm);
        setIsEdit(false);
        setItemId("");
        setErrors({});
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();
        setItemId(id);
        setOpenDialog(true);
    }

    const handleDeleteDialogConfirm = useCallback(async () => {
        if (!itemId) return;

        setFormValues(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.id === itemId
                    ? { ...item, deleted: true }
                    : item
            ),
        }));


        setOpenDialog(false);
        setItemId("");

    }, [itemId, t]);

    const handleDeleteDialogClose = useCallback(() => {
        setItemId("");
        setOpenDialog(false);
    }, []);


    const items = formValues.items.filter(i => !i.deleted);

    return (
        <>
            <div className="p-3">
                <div className="flex justify-end mb-3">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon="/media/icons/plus.svg"
                        onClick={handleAddClick}
                    >
                        Dodaj stavku
                    </Button>
                </div>
                {items && items.length > 0 && (
                    <OfferItemsAdded
                        isLoading={false}
                        items={items}
                        onEditClick={handleEditClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>
            <OfferItemForm
                show={showItemFormModal}
                onHide={handleItemFormModalClose}
                itemFormAdd={itemFormAdd}
                setItemFormAdd={setItemFormAdd}
                errors={errors}
                isEdit={isEdit}
                onSubmit={handleAddItem}
            />
            <Dialog
                open={openDialog}
                title={t(
                    "admin.fleet.vehicle.offer.sale.item.dialog.delete.title"
                )}
                message={t(
                    "admin.fleet.vehicle.offer.sale.item.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.fleet.vehicle.offer.sale.item.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.offer.sale.item.dialog.delete.canceltext"
                )}
                onConfirm={handleDeleteDialogConfirm}
                onCancel={handleDeleteDialogClose}
            />
        </>
    );
};

export default memo(OfferItems);